/**
 * Queue #275: a right-hand side panel for the Reports page.
 *
 * `components/common/Modal` is a centred dialog with no panel/side variant,
 * and giving it one would restyle every modal in the product. This is a thin
 * shell around the same headlessui `Dialog` the shared modal uses, so focus
 * trapping, escape-to-close and the overlay behave identically; only the
 * placement differs. Everything rendered INSIDE it comes from
 * `@/components/common`.
 */

import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ReportSideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
}

export function ReportSideDrawer({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
}: ReportSideDrawerProps) {
  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-out duration-200"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in duration-150"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="flex h-full w-screen max-w-xl flex-col bg-white shadow-xl dark:bg-gray-800">
                <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                  <div className="min-w-0">
                    <Dialog.Title className="text-base font-semibold text-gray-900 dark:text-gray-100">
                      {title}
                    </Dialog.Title>
                    {subtitle && (
                      <p className="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400">
                        {subtitle}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="ml-4 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
